import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

type AdministrationSection = 'users' | 'operations' | 'credits' | 'platform';

interface PrimaryTab {
  value: AdministrationSection;
  label: string;
  path: string;
}

interface SecondaryTab {
  label: string;
  path: string;
}

function getActiveSection(pathname: string): AdministrationSection {
  const section = pathname.split('/')[2];
  if (section === 'operations' || section === 'credits' || section === 'platform') return section;
  return 'users';
}

export function AdministrationLayout() {
  const { t } = useTranslation('nav');
  const location = useLocation();
  const navigate = useNavigate();
  const activeSection = getActiveSection(location.pathname);
  const primaryTabs: PrimaryTab[] = [
    { value: 'users', label: t('users'), path: '/admin/users' },
    { value: 'operations', label: t('operations'), path: '/admin/operations/projects' },
    { value: 'credits', label: t('credits'), path: '/admin/credits/overview' },
    { value: 'platform', label: t('platform'), path: '/admin/platform/providers' },
  ];
  const secondaryTabsBySection: Partial<Record<AdministrationSection, SecondaryTab[]>> = {
    operations: [
      { label: t('allProjects'), path: '/admin/operations/projects' },
      { label: t('allSessions'), path: '/admin/operations/sessions' },
    ],
    credits: [
      { label: t('credits'), path: '/admin/credits/overview' },
      { label: t('creditPlanManagement'), path: '/admin/credits/plans' },
      { label: t('resourcePlans'), path: '/admin/credits/resources' },
      { label: t('couponCodes'), path: '/admin/credits/coupons' },
    ],
    platform: [
      { label: t('apiProviders'), path: '/admin/platform/providers' },
      { label: t('modelMappings'), path: '/admin/platform/models' },
      { label: t('templateManagement'), path: '/admin/platform/templates' },
      { label: t('customDomains'), path: '/admin/platform/domains' },
      { label: t('customDomainSettings'), path: '/admin/platform/domain-settings' },
      { label: t('auditEvents'), path: '/admin/platform/audit' },
    ],
  };
  const secondaryTabs = secondaryTabsBySection[activeSection] ?? [];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">{t('administration')}</h1>
        <p className="text-sm text-muted-foreground">{t('administrationDescription')}</p>
      </header>

      <div className="space-y-3">
        <Tabs value={activeSection} onValueChange={(value) => {
          const destination = primaryTabs.find((tab) => tab.value === value)?.path;
          if (destination) navigate(destination);
        }}>
          <div className="max-w-full overflow-x-auto overflow-y-hidden">
            <TabsList className="w-max min-w-full justify-start">
              {primaryTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>

        {secondaryTabs.length > 0 ? (
          <nav className="flex max-w-full gap-1 overflow-x-auto overflow-y-hidden border-b" aria-label={t('administrationSectionNavigation')}>
            {secondaryTabs.map((tab) => {
              const active = location.pathname === tab.path;
              return (
                <button
                  key={tab.path}
                  type="button"
                  onClick={() => navigate(tab.path)}
                  className={cn(
                    'relative shrink-0 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
                    active && 'text-foreground after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary',
                  )}
                  aria-current={active ? 'page' : undefined}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        ) : null}
      </div>

      <Outlet />
    </div>
  );
}
